import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import API from './api';

const BrandingCtx = createContext(null);
const CACHE_KEY = 'pk_branding_v1';

const DEFAULT = {
  logo_text: 'PrintingKit',
  logo_tagline: 'Print-on-Demand · Delivered',
  primary_color: '#e30066',
  hero_overline: 'The Digital Press',
  hero_line_1: 'Upload.',
  hero_line_2: 'Print.',
  hero_line_3: 'Delivered in hours.',
  hero_subtext: 'From business cards to 8×10ft banners — routed to the nearest print partner and delivered to your door.',
  footer_note: 'PRINTINGKIT © 2026 · All rights reserved',
  powered_by: 'MACFINTI SOLUTIONS PVT LTD',
  contact_phone: '+91 93640 19465',
  hero_banner_link_type: 'none',
  hero_banner_link_id: '',
  banners: [],
  banners_columns: 2,
  banners_height: 'medium',
};

// Read cached branding synchronously so first paint isn't the DEFAULT placeholder.
const readCache = () => {
  try {
    const raw = typeof window !== 'undefined' ? window.localStorage.getItem(CACHE_KEY) : null;
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch { return null; }
};

export const BrandingProvider = ({ children }) => {
  const initial = readCache();
  const [branding, setBranding] = useState({ ...DEFAULT, ...(initial || {}) });
  // If we have cached branding we treat this as "loaded" so skeletons don't flash.
  const [loaded, setLoaded] = useState(Boolean(initial));

  const refresh = useCallback(async () => {
    try {
      const r = await API.get('/branding');
      const merged = { ...DEFAULT, ...r.data };
      setBranding(merged);
      try { window.localStorage.setItem(CACHE_KEY, JSON.stringify(r.data)); } catch { /* quota / private mode */ }
    } catch (e) {
      console.warn('branding fetch failed', e);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Apply primary color as CSS var + retarget #e30066 arbitrary utilities
  useEffect(() => {
    const color = branding.primary_color || '#e30066';
    document.documentElement.style.setProperty('--pk-primary', color);
    let el = document.getElementById('pk-primary-overrides');
    if (!el) {
      el = document.createElement('style');
      el.id = 'pk-primary-overrides';
      document.head.appendChild(el);
    }
    el.textContent = `
      .text-\\[\\#e30066\\] { color: ${color} !important; }
      .bg-\\[\\#e30066\\] { background-color: ${color} !important; }
      .border-\\[\\#e30066\\] { border-color: ${color} !important; }
      .pk-highlight-magenta, .pk-btn-magenta, .pk-chip.active { background-color: ${color} !important; }
    `;
  }, [branding.primary_color]);

  return (
    <BrandingCtx.Provider value={{ branding, loaded, refresh, setBranding }}>
      {children}
    </BrandingCtx.Provider>
  );
};

export const useBranding = () => useContext(BrandingCtx);
